import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { ArrowLeftIcon, CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from '../components/LoadingSpinner'
import PoolsApi from '../services/poolsApi'
import { isPoolAdmin } from '../utils/roles'

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com/v1'

interface Pool {
  poolId: string
  name: string
  description: string
  games: Game[]
}

interface Game {
  gameId: string
  homeTeam: string
  awayTeam: string
  homeConference: string
  awayConference: string
  startDate: string
  venue: string
  homeScore?: number
  awayScore?: number
  winner?: 'home' | 'away'
  status?: string
  lines?: Line[]
  mediaOutlets?: string[]
}

interface Line {
  provider: string
  spread: number
  overUnder?: number
}

export default function PoolGameResultsPage() {
  const { poolId } = useParams<{ poolId: string }>()
  const auth0 = useAuth0()
  const { user, getAccessTokenSilently } = auth0
  const navigate = useNavigate()
  const [pool, setPool] = useState<Pool | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null) // gameId being saved
  const [updatingScores, setUpdatingScores] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const userIsPoolAdmin = isPoolAdmin(user)
  const poolsApi = new PoolsApi(auth0)

  useEffect(() => {
    if (poolId && userIsPoolAdmin) {
      fetchPool()
    }
  }, [poolId, userIsPoolAdmin])

  const fetchPool = async () => {
    try {
      setLoading(true)
      const token = await getAccessTokenSilently()
      
      const response = await fetch(`${API_BASE_URL}/pools/${poolId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch pool: ${response.statusText}`)
      }

      const data = await response.json()
      
      // Check if this is a horse racing pool and redirect to correct route
      if (data.pool?.type === 'horse-racing') {
        navigate(`/admin/pools/horse-racing/${poolId}/results`)
        return
      }
      
      setPool(data.pool)
    } catch (err) {
      console.error('Error fetching pool:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch pool')
    } finally {
      setLoading(false)
    }
  }

  const handleSetResult = async (game: Game, homeScore: number, awayScore: number) => {
    if (!poolId || saving) return

    try {
      setSaving(game.gameId)
      setError(null)

      await poolsApi.setGameResult(poolId, game.gameId, {
        homeScore,
        awayScore,
        winner: homeScore > awayScore ? 'home' : 'away'
      })

      setSuccessMessage(`Result set for ${game.awayTeam} @ ${game.homeTeam}`)
      
      // Refresh pool data
      await fetchPool()

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null)
      }, 3000)

    } catch (err) {
      console.error('Error setting game result:', err)
      setError(err instanceof Error ? err.message : 'Failed to set game result')
    } finally {
      setSaving(null)
    }
  }

  const handleUpdateScores = async () => {
    if (!poolId || updatingScores) return

    try {
      setUpdatingScores(true)
      setError(null)
      setSuccessMessage(null)

      const result = await poolsApi.updateScoresFromAPI(poolId)
      
      setSuccessMessage(
        `Updated ${result.updatedGames.length} of ${result.totalGamesChecked} games with latest scores from the API`
      )
      
      // Refresh the pool data to show updated scores
      await fetchPool()
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null)
      }, 5000)

    } catch (err) {
      console.error('Error updating scores:', err)
      setError(err instanceof Error ? err.message : 'Failed to update scores from API')
    } finally {
      setUpdatingScores(false)
    }
  }

  const formatGameTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const isGameCompleted = (game: Game) => {
    return game.status === 'final' && game.winner
  }

  if (!userIsPoolAdmin) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Access Denied</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">You need pool admin privileges to access this page.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="text-sm text-red-700 dark:text-red-400">{error}</div>
        </div>
      </div>
    )
  }

  if (!pool) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Pool not found</h1>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Link 
              to={`/admin/pools/football/${poolId}`}
              className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <ArrowLeftIcon className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Game Results</h1>
              <p className="mt-1 text-lg text-gray-600 dark:text-gray-300">{pool.name}</p>
            </div>
          </div>
          
          {/* Update Scores Button */}
          <button
            onClick={handleUpdateScores}
            disabled={updatingScores}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updatingScores ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Updating...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Update Scores from API
              </>
            )}
          </button>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-green-50 dark:bg-green-900/50 border border-green-200 dark:border-green-800 rounded-md p-4">
            <div className="flex items-center">
              <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
              <div className="text-sm text-green-700 dark:text-green-400">{successMessage}</div>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4 mb-6">
          <div className="text-sm text-blue-700 dark:text-blue-300">
            <strong>Instructions:</strong> Enter the final scores for each game to update the pool standings. 
            This will determine which participants picked correctly.
          </div>
        </div>
      </div>

      {/* Games List */}
      <div className="space-y-6">
        {pool.games.map((game) => (
          <GameResultCard
            key={game.gameId}
            game={game}
            onSetResult={handleSetResult}
            saving={saving === game.gameId}
            formatGameTime={formatGameTime}
            isCompleted={isGameCompleted(game)}
          />
        ))}
      </div>

      {/* Footer Actions */}
      <div className="mt-8 flex justify-center space-x-4">
        <Link
          to={`/pools/${poolId}/standings`}
          className="inline-flex items-center px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
        >
          View Updated Standings
        </Link>
      </div>
    </div>
  )
}

interface GameResultCardProps {
  game: Game
  onSetResult: (game: Game, homeScore: number, awayScore: number) => void
  saving: boolean
  formatGameTime: (dateString: string) => string
  isCompleted: boolean
}

function GameResultCard({ game, onSetResult, saving, formatGameTime, isCompleted }: GameResultCardProps) {
  const [homeScore, setHomeScore] = useState(game.homeScore || 0)
  const [awayScore, setAwayScore] = useState(game.awayScore || 0)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSetResult(game, homeScore, awayScore)
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 ${
      isCompleted ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : ''
    }`}>
      <div className="flex justify-between items-start mb-4">
        <div>
          <div className="text-lg font-semibold text-gray-900 dark:text-white">
            {game.awayTeam} @ {game.homeTeam}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {formatGameTime(game.startDate)} • {game.venue}
          </div>
          {game.mediaOutlets && game.mediaOutlets.length > 0 && (
            <div className="text-sm text-blue-600 dark:text-blue-400 mt-1">
              {game.mediaOutlets.join(', ')}
            </div>
          )}
        </div>
        
        {isCompleted && (
          <div className="flex items-center text-green-600 dark:text-green-400">
            <CheckCircleIcon className="w-5 h-5 mr-1" />
            <span className="text-sm font-medium">Complete</span>
          </div>
        )}
        
        {!isCompleted && (
          <div className="flex items-center text-gray-500 dark:text-gray-400">
            <ClockIcon className="w-5 h-5 mr-1" />
            <span className="text-sm">Pending</span>
          </div>
        )}
      </div>

      {isCompleted ? (
        // Show final result
        <div className="grid grid-cols-2 gap-4">
          <div className={`p-4 rounded-lg text-center ${
            game.winner === 'away' ? 'bg-green-100 dark:bg-green-900 border-2 border-green-500' : 'bg-gray-100 dark:bg-gray-700'
          }`}>
            <div className="text-lg font-bold text-gray-900 dark:text-white">{game.awayTeam}</div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{game.awayScore}</div>
            {game.winner === 'away' && (
              <div className="text-sm font-medium text-green-600 dark:text-green-400 mt-1">WINNER</div>
            )}
          </div>
          <div className={`p-4 rounded-lg text-center ${
            game.winner === 'home' ? 'bg-green-100 dark:bg-green-900 border-2 border-green-500' : 'bg-gray-100 dark:bg-gray-700'
          }`}>
            <div className="text-lg font-bold text-gray-900 dark:text-white">{game.homeTeam}</div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{game.homeScore}</div>
            {game.winner === 'home' && (
              <div className="text-sm font-medium text-green-600 dark:text-green-400 mt-1">WINNER</div>
            )}
          </div>
        </div>
      ) : (
        // Show score input form
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="text-center">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {game.awayTeam} Score
              </label>
              <input
                type="number"
                min="0"
                max="999"
                value={awayScore}
                onChange={(e) => setAwayScore(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 text-center text-2xl font-bold border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                disabled={saving}
              />
            </div>
            <div className="text-center">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {game.homeTeam} Score
              </label>
              <input
                type="number"
                min="0"
                max="999"
                value={homeScore}
                onChange={(e) => setHomeScore(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 text-center text-2xl font-bold border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                disabled={saving}
              />
            </div>
          </div>
          
          <div className="text-center">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Setting Result...' : 'Set Final Result'}
            </button>
          </div>
        </form>
      )}

      {/* Game Lines Info */}
      {game.lines && game.lines.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
          <div className="text-sm text-gray-600 dark:text-gray-300">
            <strong>Betting Lines:</strong>
            {game.lines.map((line, index) => (
              <span key={index} className="ml-2">
                {line.provider}: {line.spread > 0 ? '+' : ''}{line.spread}
                {line.overUnder && ` • O/U ${line.overUnder}`}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
